/**
 * 🔄 PWA Service Worker Registration & Update Control
 * Garante que novas versões sejam aplicadas imediatamente
 * 
 * Sprint 2: Fix memory leak - interval agora é gerenciado corretamente
 * Sprint 3: Use centralized constants
 * Sprint 6: Migrado para logger centralizado
 * Sprint 7: PWA desativado em development/preview
 * Sprint 8: Cleanup automático de SW residual em dev
 * Sprint 9: Módulo 'virtual:pwa-register' isolado em pwa.prod.ts para evitar erro de build em dev
 */

import { logger } from '@/lib/logger';

// Cleanup function reference (set when prod module initializes)
let cleanupFn: (() => void) | undefined;

/**
 * 🧹 Limpa Service Workers e caches em ambiente de desenvolvimento
 * Previne que SW antigos sirvam versões desatualizadas no preview
 */
export const cleanupServiceWorkersInDev = async (): Promise<void> => {
  // Só executa em development/preview
  if (import.meta.env.PROD) return;

  try {
    // 1. Desregistrar todos os Service Workers
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      if (registrations.length > 0) {
        await Promise.all(registrations.map(reg => reg.unregister()));
        logger.info('[PWA/Dev] Service Workers residuais removidos:', registrations.length);
      }
    }

    // 2. Limpar todos os caches do PWA
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      if (cacheNames.length > 0) {
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        logger.info('[PWA/Dev] Caches limpos:', cacheNames);
      }
    }
  } catch (error) {
    logger.warn('[PWA/Dev] Erro ao limpar SW/caches:', error);
  }
};

export const initPWA = async () => {
  // PWA só é ativado em produção (o plugin VitePWA não existe em dev)
  if (!import.meta.env.PROD) {
    logger.debug('[PWA] Desativado em ambiente de desenvolvimento');
    return;
  }

  // Limpar cleanup anterior se existir (caso de hot reload)
  if (cleanupFn) {
    cleanupFn();
    cleanupFn = undefined;
  }

  try {
    // Import dinâmico do módulo prod-only que contém 'virtual:pwa-register'
    // Esse arquivo só entra no bundle de produção
    const { initPWAProd } = await import('./pwa.prod');
    cleanupFn = initPWAProd();
  } catch (error) {
    logger.warn('[PWA] Não foi possível registrar SW:', error);
  }
};

/**
 * Cleanup do PWA - chamar ao desmontar o app
 */
export const cleanupPWA = () => {
  if (cleanupFn) {
    cleanupFn();
    cleanupFn = undefined;
    logger.debug('[PWA] Cleanup executado');
  }
};

/**
 * Força limpeza completa de cache do PWA
 * Use quando o usuário estiver vendo versão antiga
 */
export const clearPWACache = async (): Promise<void> => {
  logger.info('[PWA] Iniciando limpeza de cache...');
  
  // Limpar interval antes de limpar cache
  cleanupPWA();
  
  // 1. Limpar todos os caches do Service Worker
  if ('caches' in window) {
    const cacheNames = await caches.keys();
    await Promise.all(
      cacheNames.map(cacheName => caches.delete(cacheName))
    );
    logger.debug('[PWA] Caches limpos:', cacheNames);
  }
  
  // 2. Desregistrar Service Workers atuais
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(
      registrations.map(reg => reg.unregister())
    );
    logger.debug('[PWA] Service Workers desregistrados:', registrations.length);
  }
  
  // 3. Recarregar página sem cache
  window.location.reload();
};

/**
 * Verifica se há atualizações disponíveis
 */
export const checkForUpdates = async (): Promise<void> => {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const registration of registrations) {
      await registration.update();
    }
    logger.debug('[PWA] Verificação de atualizações concluída');
  }
};
